import React, { useState, useEffect } from 'react'
import Button from '@/components/ui/Button'
import Tag from '@/components/ui/Tag'
import SidePanel from '@/components/ui/SidePanel'
import MemoModal from '@/components/ui/MemoModal'
import DateInput from '@/components/ui/DateInput'
import FileUploadSection, { type FileItem } from '@/components/ui/FileUploadSection'
import { designTokens } from '@/utils/design-tokens'
import { handleApiError } from '@/utils/api-error-handler'
import { useAlert } from '@/components/ui/Alert/exports'
import { TaskItemService } from '@/api/generated/services/TaskItemService'
import { FileService } from '@/api/generated/services/FileService'
import { TaskItemCreateRequest } from '@/api/generated/models/TaskItemCreateRequest'
import { TaskItemUpdateRequest } from '@/api/generated/models/TaskItemUpdateRequest'
import type { FileResponse } from '@/api/generated/models/FileResponse'
import type { PaginatedResponse } from '@/api/generated/models/PaginatedResponse'
import type { PresignedUploadRequest } from '@/api/generated/models/PresignedUploadRequest'
import type { PresignedUploadResponse } from '@/api/generated/models/PresignedUploadResponse'
import RequiredLabel from '@/components/ui/RequiredLabel'
import { useAppSelector } from '@/store/hooks'

// Utility function to format bytes to human-readable format
const formatFileSize = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined || bytes === 0) return '0 B'

  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  const size = bytes / Math.pow(1024, i)

  return `${size.toFixed(i === 0 ? 0 : 2)} ${sizes[i]}`
}

interface TaskItemCreatePanelProps {
  isOpen: boolean
  onClose: () => void
  taskId: string
  taskName: string
  taskSubgroupName: string
  businessName: string
  onSuccess?: (createdId?: string) => void
  // 수정 모드를 위한 추가 props
  editMode?: boolean
  taskItemId?: string
  initialTaskItemData?: {
    name: string
    startAt: string
    endAt: string
    output: string
  }
}

// Section Header Component
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div 
    className="pb-2 border-b" 
    style={{ 
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3 
      style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '18px',
        fontWeight: 700,
        color: designTokens.colors.gray[6]
      }}
    >
      {title}
    </h3>
  </div>
)

// Info Field Component
const InfoField: React.FC<{ 
  label: string
  value: string | React.ReactNode
  fullWidth?: boolean 
}> = ({ label, value, fullWidth = false }) => (
  <div className={`flex ${fullWidth ? 'flex-col gap-2' : 'items-center gap-5'}`}>
    <div className={fullWidth ? '' : 'w-37 flex-shrink-0'}>
      <span 
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '16px',
          fontWeight: 400,
          color: designTokens.colors.gray[6]
        }}
      >
        {label}
      </span>
    </div>
    <div className={fullWidth ? '' : 'flex-1 py-2'}>
      {typeof value === 'string' ? (
        <span 
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '16px',
            fontWeight: 400,
            color: designTokens.colors.gray[6]
          }}
        >
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  </div>
)

const TaskItemCreatePanel: React.FC<TaskItemCreatePanelProps> = ({
  isOpen,
  onClose,
  taskId,
  taskName,
  taskSubgroupName,
  businessName,
  onSuccess,
  editMode = false,
  taskItemId,
  initialTaskItemData
}) => {
  const [taskItemName, setTaskItemName] = useState(initialTaskItemData?.name || '')
  const [startAt, setStartAt] = useState(initialTaskItemData?.startAt || '')
  const [endAt, setEndAt] = useState(initialTaskItemData?.endAt || '')
  const [output, setOutput] = useState(initialTaskItemData?.output || '')
  const [attachedFiles, setAttachedFiles] = useState<FileItem[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isMemoModalOpen, setIsMemoModalOpen] = useState(false)
  const [startAtError, setStartAtError] = useState<string | null>(null)
  const [endAtError, setEndAtError] = useState<string | null>(null)

  // 기존 파일 목록 관련 state
  const [existingFiles, setExistingFiles] = useState<FileResponse[]>([])
  const [isLoadingFiles, setIsLoadingFiles] = useState(false)
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null)

  // 파일 업로드 관련 state
  const [uploadingFiles, setUploadingFiles] = useState<Set<number>>(new Set())
  const [uploadProgress, setUploadProgress] = useState<Record<number, number>>({})

  const { showError, showSuccess } = useAlert()

  // Redux store에서 현재 워크스페이스 정보 가져오기
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)

  // 수정 모드일 때 초기값 설정
  useEffect(() => {
    if (editMode && initialTaskItemData) {
      setTaskItemName(initialTaskItemData.name)
      setStartAt(initialTaskItemData.startAt)
      setEndAt(initialTaskItemData.endAt)
      setOutput(initialTaskItemData.output)
    }
  }, [editMode, initialTaskItemData])

  // 수정 모드일 때 기존 파일 목록 가져오기
  useEffect(() => {
    const fetchExistingFiles = async () => {
      if (!editMode || !taskItemId || !isOpen) {
        setExistingFiles([])
        return
      }

      setIsLoadingFiles(true)

      try {
        const response = await FileService.appsFileViewsListFilesByTarget({
          targetId: taskItemId,
          targetType: 'task_item',
          page: 1,
          perPage: 100
        })

        if (response && Array.isArray(response.data)) {
          setExistingFiles(response.data as FileResponse[])
        } else {
          setExistingFiles([])
        }
      } catch (error) {
        console.error('Failed to fetch existing files:', error)
        setExistingFiles([])
      } finally {
        setIsLoadingFiles(false)
      }
    }

    fetchExistingFiles()
  }, [editMode, taskItemId, isOpen])

  // 기존 파일 삭제 함수
  const handleDeleteExistingFile = async (file: FileResponse) => {
    setDeletingFileId(file.id)

    try {
      await FileService.appsFileViewsDeleteFile({
        fileId: file.id
      })

      // 목록에서 제거
      setExistingFiles(prev => prev.filter(f => f.id !== file.id))
      showSuccess('파일이 삭제되었습니다.')
    } catch (error) {
      console.error('Failed to delete file:', error)
      showError('파일 삭제에 실패했습니다.')
    } finally {
      setDeletingFileId(null)
    }
  }

  // 파일 업로드 함수
  const uploadFiles = async (targetId: string, targetType: string, workspaceId: string) => {
    if (attachedFiles.length === 0) {
      return
    }

    const uploadPromises = attachedFiles.map(async (fileItem, index) => {
      try {
        setUploadingFiles(prev => new Set(prev).add(index))
        setUploadProgress(prev => ({ ...prev, [index]: 0 }))

        // 1. Presigned URL 요청
        const presignedRequest: PresignedUploadRequest = {
          file_name: fileItem.file.name,
          target_type: targetType,
          target_id: targetId,
          workspace_id: workspaceId
        }

        const presignedResponse: PresignedUploadResponse = await FileService.appsFileViewsGetPresignedUploadUrl({
          requestBody: presignedRequest
        })

        setUploadProgress(prev => ({ ...prev, [index]: 25 }))

        // 2. 파일 업로드
        if (presignedResponse.method === 'POST' && presignedResponse.upload_fields) {
          // S3 POST 업로드 (FormData)
          const formData = new FormData()

          // upload_fields를 먼저 추가
          Object.entries(presignedResponse.upload_fields).forEach(([key, value]) => {
            formData.append(key, value as string)
          })

          // 파일을 마지막에 추가
          formData.append('file', fileItem.file)

          setUploadProgress(prev => ({ ...prev, [index]: 50 }))

          const uploadResponse = await fetch(presignedResponse.upload_url, {
            method: 'POST',
            body: formData
          })

          if (!uploadResponse.ok) {
            throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`)
          }
        } else {
          // PUT 업로드 (직접 파일)
          setUploadProgress(prev => ({ ...prev, [index]: 50 }))

          const uploadResponse = await fetch(presignedResponse.upload_url, {
            method: presignedResponse.method,
            body: fileItem.file,
            headers: {
              'Content-Type': presignedResponse.content_type
            }
          })

          if (!uploadResponse.ok) {
            throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`)
          }
        }

        setUploadProgress(prev => ({ ...prev, [index]: 100 }))
        console.log(`File uploaded successfully: ${fileItem.file.name}`)
      } catch (error) {
        console.error(`Failed to upload file ${fileItem.file.name}:`, error)
        throw error
      } finally {
        setUploadingFiles(prev => {
          const newSet = new Set(prev)
          newSet.delete(index)
          return newSet
        })
      }
    })

    await Promise.all(uploadPromises)

    // 업로드 완료 후 progress 초기화
    setUploadProgress({})
  }

  const handleCancel = () => {
    setTaskItemName('')
    setStartAt('')
    setEndAt('')
    setOutput('')
    setAttachedFiles([])
    setExistingFiles([])
    setStartAtError(null)
    setEndAtError(null)
    setUploadingFiles(new Set())
    setUploadProgress({})
    onClose()
  }

  const handleSave = async () => {
    if (!taskItemName.trim()) {
      showError('수행업무명을 입력해주세요.')
      return
    }

    if (!startAt) {
      showError('시작일을 입력해주세요.')
      return
    }

    if (!endAt) {
      showError('종료일을 입력해주세요.')
      return
    }

    // 날짜 검증
    const startDate = new Date(startAt)
    const endDate = new Date(endAt)
    if (startDate > endDate) {
      showError('시작일은 종료일보다 빨라야 합니다.')
      setEndAtError('종료일은 시작일보다 늦어야 합니다')
      return
    }

    // 에러 상태 초기화
    setStartAtError(null)
    setEndAtError(null)

    if (!editMode && !taskId) {
      showError('세부실행과제가 선택되지 않았습니다.')
      return
    }

    // 파일 업로드가 있는 경우 워크스페이스 확인
    if (attachedFiles.length > 0 && !selectedWorkspace?.id) {
      showError('워크스페이스가 선택되지 않았습니다. 파일 업로드를 위해 워크스페이스를 선택해주세요.')
      return
    }

    // 수정 모드: 메모 입력 모달을 띄운 뒤 저장
    if (editMode && taskItemId) {
      setIsMemoModalOpen(true)
      return
    }

    await performSave()
  }

  // 실제 생성/수정 수행. 수정 모드에서는 메모 모달 확인 시 memo와 함께 호출된다.
  const performSave = async (memo?: string) => {
    setIsSubmitting(true)

    try {
      let createdTaskItemId: string = taskItemId || ''

      if (editMode && taskItemId) {
        // 수정 모드: Update API 호출
        const updateRequest: TaskItemUpdateRequest = {
          name: taskItemName.trim(),
          start_at: startAt,
          end_at: endAt,
          output: output.trim(),
          memo
        }

        await TaskItemService.appsTaskViewsUpdateTaskItem({
          itemId: taskItemId,
          requestBody: updateRequest
        })

        createdTaskItemId = taskItemId
      } else {
        // 생성 모드: Create API 호출
        const createRequest: TaskItemCreateRequest = {
          task_id: taskId,
          name: taskItemName.trim(),
          start_at: startAt,
          end_at: endAt,
          output: output.trim()
        }

        const createResponse = await TaskItemService.appsTaskViewsCreateTaskItem({
          requestBody: createRequest
        })

        // 생성된 task item의 ID를 가져와야 함 (API 응답 구조에 따라 조정 필요)
        createdTaskItemId = createResponse.id || createResponse.data?.id || ''
      }

      // 파일 업로드 처리 (첨부된 파일이 있는 경우만)
      if (attachedFiles.length > 0 && createdTaskItemId && selectedWorkspace?.id) {
        await uploadFiles(createdTaskItemId, 'task_item', selectedWorkspace.id)
      }

      // Success - show message and call onSuccess first before closing
      const successMessage = attachedFiles.length > 0
        ? `수행업무가 ${editMode ? '수정' : '생성'}되고 파일이 업로드되었습니다.`
        : `수행업무가 ${editMode ? '수정' : '생성'}되었습니다.`
      showSuccess(successMessage)

      // Call onSuccess before closing so parent can access state
      // Wait for onSuccess to complete if it's async (especially for edit mode)
      if (onSuccess) {
        await onSuccess(createdTaskItemId)
      }

      // Reset form and close panel
      setIsMemoModalOpen(false)
      setTaskItemName('')
      setStartAt('')
      setEndAt('')
      setOutput('')
      setAttachedFiles([])
      setStartAtError(null)
      setEndAtError(null)
      setUploadingFiles(new Set())
      setUploadProgress({})
      onClose()
    } catch (err) {
      console.error(`Failed to ${editMode ? 'update' : 'create'} task item:`, err)

      // Use common error handler
      const errorPrefix = editMode
        ? '수행업무 수정에 실패했습니다'
        : '수행업무 생성에 실패했습니다'
      handleApiError(err, showError, errorPrefix)
    } finally {
      setIsSubmitting(false)
    }
  }

  const headerActions = (
    <>
      <Button
        size="small"
        color="white"
        onClick={handleCancel}
        disabled={isSubmitting}
        className="flex items-center gap-1"
      >
        취소
      </Button>
      <Button
        size="small"
        color="dark"
        onClick={handleSave}
        disabled={isSubmitting || !taskItemName.trim() || !startAt || !endAt || uploadingFiles.size > 0}
        className="flex items-center gap-1"
      >
        {isSubmitting ? (
          uploadingFiles.size > 0
            ? `파일 업로드 중... (${Object.keys(uploadProgress).length}/${attachedFiles.length})`
            : '저장 중...'
        ) : '저장'}
      </Button>
    </>
  )

  return (
    <>
    <SidePanel
      isOpen={isOpen}
      onClose={handleCancel}
      title={editMode ? "수행업무 수정" : "수행업무 생성"}
      width={800}
      headerActions={headerActions}
    >
      <div 
        className="flex flex-col space-y-2" 
        style={{ 
          padding: '24px',
        }}
      >
        {/* Section: 기본정보 */}
        <div className="space-y-2">
          <SectionHeader title="기본정보" />
          <div className="space-y-2" style={{ padding: '0px 4px' }}>
            <InfoField 
              label="유형" 
              value={
                <Tag color="gray" size="small">
                  ● 수행업무
                </Tag>
              }
            />
            <InfoField 
              label="계층" 
              value={`${businessName} > ${taskSubgroupName} > ${taskName}`}
            />
            <InfoField 
              label={<RequiredLabel>수행업무명</RequiredLabel>}
              value={
                <input
                  type="text"
                  value={taskItemName}
                  onChange={(e) => setTaskItemName(e.target.value)}
                  placeholder="수행업무명을 입력하세요"
                  disabled={isSubmitting}
                  className="w-full p-3 border rounded"
                  style={{
                    borderColor: designTokens.colors.gray[1],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '16px',
                    color: designTokens.colors.gray[6]
                  }}
                />
              }
              fullWidth
            />
          </div>
        </div>

        {/* Section: 일정 */}
        <div className="space-y-2">
          <SectionHeader title="일정" />
          <div className="space-y-2" style={{ padding: '0px 4px' }}>
            <InfoField
              label={<RequiredLabel>시작일</RequiredLabel>}
              value={
                <DateInput
                  value={startAt}
                  onChange={(value) => {
                    setStartAt(value)
                    setStartAtError(null)
                    // 시작일이 변경되면 종료일과의 관계 검증
                    if (endAt && value && new Date(value) > new Date(endAt)) {
                      setEndAtError('종료일은 시작일보다 늦어야 합니다')
                    } else {
                      setEndAtError(null)
                    }
                  }}
                  placeholder="YYYY-MM-DD 또는 YYYY.MM.DD 형식으로 입력"
                  disabled={isSubmitting}
                  required
                  error={startAtError}
                  className="w-full"
                />
              }
              fullWidth
            />
            <InfoField
              label={<RequiredLabel>종료일</RequiredLabel>}
              value={
                <DateInput
                  value={endAt}
                  onChange={(value) => {
                    setEndAt(value)
                    setEndAtError(null)
                    // 종료일이 변경되면 시작일과의 관계 검증
                    if (startAt && value && new Date(startAt) > new Date(value)) {
                      setEndAtError('종료일은 시작일보다 늦어야 합니다')
                    } else {
                      setEndAtError(null)
                    }
                  }}
                  placeholder="YYYY-MM-DD 또는 YYYY.MM.DD 형식으로 입력"
                  disabled={isSubmitting}
                  required
                  error={endAtError}
                  className="w-full"
                />
              }
              fullWidth
            />
          </div>
        </div>

        {/* Section: 산출물 */}
        <div className="space-y-2">
          <SectionHeader title="성과물" />
          <div className="space-y-2" style={{ padding: '0px 4px' }}>
            <InfoField
              label="성과물"
              value={
                <textarea
                  value={output}
                  onChange={(e) => setOutput(e.target.value)}
                  placeholder="성과물을 입력하세요"
                  disabled={isSubmitting}
                  className="w-full p-3 border rounded resize-none"
                  rows={4}
                  style={{
                    borderColor: designTokens.colors.gray[1],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '16px',
                    color: designTokens.colors.gray[6]
                  }}
                />
              }
              fullWidth
            />
          </div>
        </div>

        {/* Section: 첨부파일 */}
        <div className="mt-6">
          <SectionHeader title="첨부파일" />
          <div className="mt-4">
            {/* 수정 모드에서 기존 파일 목록 표시 */}
            {editMode && (
              <div className="mb-4">
                {isLoadingFiles ? (
                  <div style={{
                    padding: '16px',
                    textAlign: 'center',
                    color: designTokens.colors.gray[4]
                  }}>
                    업로드된 파일을 불러오는 중...
                  </div>
                ) : existingFiles.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    <div
                      style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '14px',
                        fontWeight: 600,
                        color: designTokens.colors.gray[5],
                        marginBottom: '8px'
                      }}
                    >
                      기존에 업로드된 파일 ({existingFiles.length}개)
                    </div>
                    {existingFiles.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center justify-between p-3 border rounded"
                        style={{
                          borderColor: designTokens.colors.gray[1],
                          backgroundColor: designTokens.colors.gray[0]
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: designTokens.colors.success.DEFAULT }}
                          />
                          <div>
                            <div
                              style={{
                                fontFamily: designTokens.typography.fontFamily.sans[0],
                                fontSize: '16px',
                                fontWeight: 600,
                                color: designTokens.colors.gray[6]
                              }}
                            >
                              {file.origin_file_name}
                            </div>
                            <div
                              style={{
                                fontFamily: designTokens.typography.fontFamily.sans[0],
                                fontSize: '14px',
                                fontWeight: 400,
                                color: designTokens.colors.gray[4]
                              }}
                            >
                              {file.file_extension.toUpperCase()} 파일 • {formatFileSize(file.content_size)}
                            </div>
                          </div>
                        </div>
                        <Button
                          size="small"
                          color="danger"
                          onClick={() => handleDeleteExistingFile(file)}
                          disabled={deletingFileId === file.id}
                        >
                          {deletingFileId === file.id ? '삭제 중...' : '삭제'}
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            )}

            {/* 파일 업로드 영역 */}
            <FileUploadSection
              files={attachedFiles}
              onChange={setAttachedFiles}
              maxFiles={100}
              maxFileSize={1000}
              disabled={isSubmitting || uploadingFiles.size > 0}
            />

            {/* 업로드 진행 상황 표시 */}
            {uploadingFiles.size > 0 && (
              <div className="mt-4 space-y-2">
                <div
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '14px',
                    fontWeight: 600,
                    color: designTokens.colors.gray[5]
                  }}
                >
                  파일 업로드 중...
                </div>
                {Array.from(uploadingFiles).map((index) => {
                  const file = attachedFiles[index]
                  const progress = uploadProgress[index] || 0
                  return (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-2 border rounded"
                      style={{
                        borderColor: designTokens.colors.gray[1],
                        backgroundColor: designTokens.colors.gray[0]
                      }}
                    >
                      <div className="flex-1">
                        <div
                          style={{
                            fontFamily: designTokens.typography.fontFamily.sans[0],
                            fontSize: '14px',
                            fontWeight: 600,
                            color: designTokens.colors.gray[6]
                          }}
                        >
                          {file.file.name}
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div
                            className="h-2 rounded-full transition-all duration-300"
                            style={{
                              width: `${progress}%`,
                              backgroundColor: designTokens.colors.primary.DEFAULT
                            }}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '12px',
                          color: designTokens.colors.gray[4]
                        }}
                      >
                        {progress}%
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </SidePanel>

    <MemoModal
      isOpen={isMemoModalOpen}
      onClose={() => setIsMemoModalOpen(false)}
      onConfirm={performSave}
      title="수행업무 수정"
      isSubmitting={isSubmitting}
    />
    </>
  )
}

export default TaskItemCreatePanel